import React, { useState } from 'react';
import { Shield, Check, HelpCircle, ChevronDown, ChevronUp, Target, Clock, Award, FileCheck, Users, Zap } from 'lucide-react';
import UniversalPopupForm from '../../components/UniversalPopupForm';
import PricingSection from '../../components/PricingSection';
import SEO from '../../components/SEO';
import { usePricing } from '../../context/PricingContext';

const TrademarkRegistration = () => {
  const { resetPackage } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    {
      title: 'Exclusive Rights',
      description: 'Gain exclusive rights to use your trademark across Canada for your goods or services.'
    },
    {
      title: 'Legal Protection',
      description: 'Protect your brand from unauthorized use and potential infringement by competitors.'
    },
    {
      title: 'Business Value',
      description: 'Increase your business value with a registered trademark that can be licensed or sold.'
    },
    {
      title: 'CIPO Recognition',
      description: 'Official recognition from the Canadian Intellectual Property Office for your brand.'
    },
    {
      title: 'National Coverage',
      description: 'Protection extends across all Canadian provinces and territories.'
    },
    {
      title: 'Brand Trust',
      description: 'Build customer confidence with the ® symbol next to your brand name.'
    }
  ];

  const faqs = [
    {
      question: 'How long does the Canadian trademark registration process take?',
      answer: 'The CIPO trademark registration process typically takes 12-18 months from application to final registration. However, you can start using the ™ symbol immediately after filing.'
    },
    {
      question: 'What can be trademarked in Canada?',
      answer: 'In Canada, you can trademark words, logos, slogans, designs, shapes, sounds, and even colors that distinguish your goods or services. The mark must be distinctive and not confusingly similar to existing trademarks.'
    },
    {
      question: 'Do I need a lawyer to register a trademark?',
      answer: 'While not legally required, working with trademark professionals significantly increases your chances of approval. Our experts handle all CIPO requirements and paperwork for you.'
    },
    {
      question: 'What are the government fees for trademark registration?',
      answer: 'CIPO charges government filing fees in addition to our service fees. These fees vary based on the number of classes you\'re registering. We\'ll provide exact government fee amounts during consultation.'
    },
    {
      question: 'Can I trademark a business name?',
      answer: 'Yes, business names can be trademarked in Canada if they meet CIPO\'s requirements for distinctiveness. We\'ll conduct a comprehensive search to ensure your business name qualifies.'
    },
    {
      question: 'What happens if my trademark application is refused?',
      answer: 'If CIPO raises objections, our experts will help respond to office actions and work to overcome any refusals. Our Deluxe and Platinum packages include dedicated support for handling refusals.'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Trademark Registration Services"
        description="Professional Canadian trademark registration services. Gain exclusive rights to your brand name and logo across Canada with expert CIPO filing assistance."
        keywords="trademark registration Canada, CIPO trademark filing, register brand name, business trademark protection, Canadian IP services"
      />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1638262052640-82e94d64664a"
            alt="Professional handshake"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-white bg-opacity-20 rounded-full px-4 py-2 mb-6">
              <span className="text-white font-semibold">CIPO Trademark Registration</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">
              Canadian Trademark Registration
            </h1>
            <p className="text-xl text-brand-100 leading-relaxed mb-8">
              Protect your brand with official CIPO trademark registration. Fast, reliable, and affordable services for Canadian businesses.
            </p>
            <button
              onClick={() => { resetPackage(); setIsPopupOpen(true); }}
              className="bg-white text-brand-600 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-xl"
            >
              Start Your Registration
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Professional Help - Image Left, Content Right - MOVED HERE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1638262052640-82e94d64664a"
                alt="Professional trademark consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-green-100 rounded-full p-2">
                    <Award className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="font-bold text-gray-900">98% Success Rate</span>
                </div>
                <p className="text-sm text-gray-600">Expert CIPO filing & approval</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Advantage
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Professional Help Matters
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                DIY trademark applications have a significantly higher rejection rate. Our CIPO experts ensure your application is done right the first time, saving you time, money, and frustration.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Target className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Strategic Classification</h3>
                    <p className="text-gray-600">Proper class selection ensures comprehensive protection for your specific goods and services.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Thorough Documentation</h3>
                    <p className="text-gray-600">All CIPO requirements met with precision, minimizing rejection risks.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Save Time & Money</h3>
                    <p className="text-gray-600">Avoid costly mistakes and months of back-and-forth with CIPO.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Trademark Registration
            </h2>
            <p className="text-lg text-gray-600">
              Why every Canadian business needs trademark protection
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-brand-100 min-w-full md:min-w-0 snap-center"
              >
                <div className="bg-brand-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CIPO Process Details - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-green-100 text-green-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                CIPO Expertise
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Navigate CIPO Requirements With Confidence
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                The Canadian Intellectual Property Office has specific requirements and procedures. Our team has successfully filed over 500 trademarks and knows exactly what CIPO looks for.
              </p>
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl">What We Handle:</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Comprehensive trademark availability search</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Proper goods/services classification</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Complete application preparation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Response to office actions</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Ongoing application monitoring</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { resetPackage(); setIsPopupOpen(true); }}
                className="bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Start Your Application
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-500 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/8441784/pexels-photo-8441784.jpeg"
                alt="CIPO filing process"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">CIPO Certified</div>
                    <div className="text-sm text-gray-600">Experts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fast & Reliable Service - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/8171203/pexels-photo-8171203.jpeg"
                alt="Fast processing"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-brand-600 to-brand-800 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <Zap className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">24-48 Hour Response</div>
                <div className="text-sm text-brand-100">Lightning-fast service</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Quick & Efficient
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fast Processing, Quality Results
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We understand that time is money. Our streamlined process ensures your trademark application is prepared and filed quickly without compromising quality or accuracy.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Quick Turnaround</h3>
                    <p className="text-gray-600">Initial consultation and trademark search completed within 24-48 hours.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Dedicated Support</h3>
                    <p className="text-gray-600">Personal case manager keeps you updated throughout the entire process.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Quality Guaranteed</h3>
                    <p className="text-gray-600">Our 98% success rate speaks to our commitment to excellence.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* FAQs Section - Creative with Image */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about Canadian trademark registration
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left - Image */}
            <div className="relative lg:sticky lg:top-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72"
                alt="Professional consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Still Have Questions?</h3>
                <p className="text-gray-600 text-sm mb-4">Our trademark experts are here to help answer all your CIPO registration questions.</p>
                <button
                  onClick={() => { resetPackage(); setIsPopupOpen(true); }}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold py-2 px-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transition-all text-sm"
                >
                  Contact Us
                </button>
              </div>
            </div>

            {/* Right - FAQs */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <HelpCircle className="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                    </div>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <div className="pl-10 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Creative with Canadian Background */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1714428586252-d72da2ece0a0"
            alt="Toronto skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/95 via-brand-800/90 to-brand-700/95"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-900/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Trusted by 500+ Canadian Businesses</span>
            </div>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Secure Your Brand's Future<br />
            <span className="bg-gradient-to-r from-brand-300 to-brand-400 bg-clip-text text-transparent">
              With Canadian Trademark Protection
            </span>
          </h2>
          
          <p className="text-2xl text-brand-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Protect your brand with CIPO registration. Start your trademark journey today with Canada's trusted experts.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <button
              onClick={() => { resetPackage(); setIsPopupOpen(true); }}
              className="group relative bg-white text-brand-600 font-bold px-10 py-5 rounded-xl hover:bg-gray-100 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Shield className="w-6 h-6" />
                <span className="text-lg">Register Your Trademark</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-brand-400 to-brand-500 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300"></div>
            </button>
            <button
              onClick={() => {
                if (window.LiveChatWidget) {
                  window.LiveChatWidget.call('maximize');
                } else {
                  alert('Chat is loading... Please try again in a moment.');
                }
              }}
              className="group relative bg-gradient-to-r from-brand-600 to-brand-800 text-white font-bold px-10 py-5 rounded-xl hover:from-brand-700 hover:to-brand-900 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 text-lg">Chat Now</span>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">98%</div>
              <div className="text-brand-100 text-sm">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">24h</div>
              <div className="text-brand-100 text-sm">Fast Response</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">500+</div>
              <div className="text-brand-100 text-sm">Brands Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">10+</div>
              <div className="text-brand-100 text-sm">Provinces Served</div>
            </div>
          </div>

          <p className="text-brand-200 mt-12 text-sm">
            Protecting Canadian businesses coast to coast • CIPO Registered Agents
          </p>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-brand-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-brand-400/20 rounded-full blur-3xl animate-pulse"></div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default TrademarkRegistration;
